import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { Roles } from '~/data/user';
import { HeadcountReport } from '~/scenes/PortalModules/Analytics/People/HeadcountReport';
import { PersonnelListCustomReport } from '~/scenes/PortalModules/Analytics/People/PersonnelListCustomReport';
import { PersonnelListReport } from '~/scenes/PortalModules/Analytics/People/PersonnelListReport';
import { CompensationEquityReport } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEquityReport';
import {
  NavigationRoutingStructure,
  useStructuredModuleRouting,
} from '~/scenes/PortalViews/components/StructuredModuleRouter';
import { hasOneOfRoles } from '~/services/Permissions';

export const useAnalyticsViews = () => {
  const { t } = useTranslation();

  const myRoles = useLoggedInUserRoles();

  const hasRoleAccess = useCallback(
    (roles: Roles[]) => {
      return hasOneOfRoles(myRoles, roles);
    },
    [myRoles],
  );

  /**
   * Old roles mapping
   * iHR = HR_STAFF, HR_ADMIN, HR_ADMIN_LIMITED, HR_DEV (Included but might be a mistake)
   * iManager = MANAGER, ALT_MANAGER
   * ROLE-HR_STAFF-MANAGER = iHR & iManager
   */

  const initialNavigationStructure: NavigationRoutingStructure[] =
    useMemo(() => {
      const fullHRs = [Roles.HR_ADMIN, Roles.HR_ADMIN_LIMITED, Roles.HR_STAFF];
      const fullManagers = [Roles.MANAGER, Roles.ALT_MANAGER];
      const fullManagersAndHRs = [...fullHRs, ...fullManagers];

      return [
        {
          path: 'company-dashboard',
          label: t('laCompDB'),
        },
        {
          path: 'team-dashboard',
          label: t('laTeamDB'),
          isAvailable: hasRoleAccess([
            ...fullManagersAndHRs,
            Roles.DEPUTY_MANAGER,
          ]),
          subPaths: [
            {
              path: 'team-report',
              label: t('laTeamRep'),
              isAvailable: hasRoleAccess(fullManagersAndHRs),
            },
            {
              path: 'absence-calendar',
              label: t('laAbsCal'),
              isAvailable: hasRoleAccess([
                ...fullManagersAndHRs,
                Roles.DEPUTY_MANAGER,
              ]),
            },
          ],
        },
        {
          path: 'hr-dashboard',
          label: t('laHRDB'),
          isAvailable: hasRoleAccess([...fullHRs, Roles.HR_DEV]),
        },
        {
          path: 'employee-dashboard',
          label: t('laEmpDB'),
        },
        {
          path: 'work-experience-dashboard',
          label: t('laWEDB'),
        },
        {
          path: 'people',
          label: t('laPeople'),
          subPaths: [
            {
              path: 'test',
              label: 'Test',
              component: <>Lorem ipsum</>,
              isAvailable: false,
            },
            {
              path: 'headcount-report',
              label: t('laHeadCountReport'),
              component: <HeadcountReport />,
              isAvailable: hasRoleAccess([...fullManagersAndHRs, Roles.HR_DEV]),
            },
            {
              path: 'personnel-list',
              label: t('laPersonelList'),
              component: <PersonnelListReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndHRs,
                Roles.HR_DEV,
                Roles.IT_ADMIN,
              ]),
            },
            {
              path: 'personnel-list-custom',
              label: t('laCustomPersonelList'),
              component: <PersonnelListCustomReport />,
              isAvailable: hasRoleAccess(fullManagersAndHRs),
            },
          ],
        },
        {
          path: 'rewarding',
          label: t('laRewarding'),
          isAvailable: hasRoleAccess(fullManagersAndHRs),
          subPaths: [
            {
              path: 'compensation-equity',
              label: t('compensationEquity'),
              component: <CompensationEquityReport />,
            },
          ],
        },
        {
          path: 'performance',
          label: t('laPerformance'),
          isAvailable: hasRoleAccess(fullManagersAndHRs),
        },
        {
          path: 'development',
          label: t('laDevelopment'),
          isAvailable: hasRoleAccess([...fullManagersAndHRs, Roles.IT_ADMIN]),
        },
        // {
        //   path: 'organization',
        //   label: t('laOrganization'),
        // },
        // {
        //   path: 'succession',
        //   label: t('laSuccPlan'),
        // },
      ];
    }, [t, hasRoleAccess]);

  return useStructuredModuleRouting(initialNavigationStructure);
};
